import React, { useState } from 'react';
import { BsStars } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Box, Button, Flex, Progress, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIOutlineGradientButton from '../../../components/PINSAIOutlineGradientButton';
import { ClassificationBusyStatuses, ImportStatuses } from '../../../constants/importStatuses';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { showConfirmModal, showModal } from '../../../redux/modules/modal';
import { downloadImportManifest, useImportAction, useImportFiles } from '../../../services/import-service';

import FillBlanksModal from './FillBlanksModal';
import { acceptsUploads, hasManifest, humanize } from './util';

const LOCKED_IMPORT_STATUSES = [ImportStatuses.validating, ImportStatuses.running, ImportStatuses.rollingBack];

/**
 * The manifest helpers that sit between the dry run and the rows: draft or
 * re-draft the manifest from the staged files, download it, and fill its blanks
 * from the files with AI. `tools` narrows them to the ones a wizard step owns.
 */
const ImportManifestTools = ({ importData, tools = ['draft', 'download', 'fill'] }) => {
  const dispatch = useDispatch();
  const [downloading, setDownloading] = useState(false);

  const { data: files } = useImportFiles(importData.id);
  const { mutateAsync: act, isLoading: drafting } = useImportAction(importData.id);

  const enabled = acceptsUploads(importData);
  const stagedCount = files?.total ?? 0;
  const classification = importData.summary?.classification;
  // the request is in flight until the first poll brings back the queued pass
  const requesting = importData.pendingAction === 'classify';
  const labelling = requesting || ClassificationBusyStatuses.includes(classification?.status);
  const labellingPercent = classification?.total ? Math.round(((classification.done ?? 0) / classification.total) * 100) : 0;
  const canLabel = !LOCKED_IMPORT_STATUSES.includes(importData.status) && (importData.rowCounts?.total ?? 0) > 0 && !labelling;

  const draft = async () => {
    try {
      await act('draftManifest');
      dispatch(createSuccessToast({ title: 'Manifest drafted from the staged files', description: 'Review the blanks, then validate.' }));
    } catch (error) {
      dispatch(createErrorToast({ title: 'The manifest could not be drafted', description: error?.message, error }));
    }
  };

  const handleDraft = () => {
    if (hasManifest(importData)) {
      dispatch(showConfirmModal('Replace the current manifest with a fresh draft from the staged files?', draft));
      return;
    }

    draft();
  };

  const handleDownloadManifest = async () => {
    setDownloading(true);

    try {
      await downloadImportManifest(importData.id);
    } catch (error) {
      dispatch(createErrorToast({ title: 'Could not download the manifest', description: error?.message, error }));
    } finally {
      setDownloading(false);
    }
  };

  const handleLabel = () => {
    dispatch(showModal(FillBlanksModal, { title: 'Fill in the blanks with AI', size: 'lg' }, { importId: importData.id }));
  };

  const handleCancelFill = async () => {
    try {
      await act('cancelFill');
      dispatch(createSuccessToast({ title: 'Fill in the blanks cancelled', description: 'Nothing it read was written.' }));
    } catch (error) {
      dispatch(createErrorToast({ title: 'The pass could not be cancelled', description: error?.message, error }));
    }
  };

  const showDraft = tools.includes('draft') && enabled && stagedCount > 0;
  const showDownload = tools.includes('download') && hasManifest(importData);
  const showFill = tools.includes('fill');
  const showLabel = showFill && (importData.rowCounts?.total ?? 0) > 0;

  if (!showDraft && !showDownload && !showLabel && !(showFill && classification)) {
    return null;
  }

  return (
    <Box>
      <Flex alignItems="center" flexWrap="wrap" gap={3}>
        {showDraft && (
          <Button data-testid="draft-manifest" isLoading={drafting} size="sm" variant="secondary" onClick={handleDraft}>
            {hasManifest(importData) ? 'Re-draft the manifest from the files' : 'Draft a manifest from the uploaded files'}
          </Button>
        )}
        {showDownload && (
          <Button data-testid="download-manifest" isLoading={downloading} size="sm" variant="textGrey" onClick={handleDownloadManifest}>
            Download manifest
          </Button>
        )}
        {showLabel && (
          <PINSAIOutlineGradientButton
            data-testid="label-with-ai"
            isDisabled={!canLabel}
            isLoading={labelling}
            leftIcon={BsStars}
            mt={0}
            size="sm"
            text="Fill in the Blanks"
            w="auto"
            onClick={handleLabel}
          />
        )}
        {showFill && labelling && !requesting && (
          <Button data-testid="cancel-fill" isLoading={importData.pendingAction === 'cancelFill'} size="sm" variant="textGrey" onClick={handleCancelFill}>
            Cancel
          </Button>
        )}
      </Flex>

      {showFill && labelling && (
        <Progress
          hasStripe
          isAnimated
          borderRadius="md"
          data-testid="classification-progress-bar"
          isIndeterminate={requesting || classification?.status === 'queued' || !classification?.total}
          mt={3}
          size="sm"
          value={labellingPercent}
        />
      )}

      {showFill && classification && (
        <Text color={classification.status === 'failed' ? 'red.600' : 'greyScale.500'} data-testid="classification-progress" mt={2} textStyle="bodySm">
          {classification.status === 'failed'
            ? `Fill in the blanks failed: ${classification.error ?? 'unknown error'}`
            : `Fill in the blanks ${humanize(classification.status)}: ${classification.done ?? 0} of ${classification.total ?? 0} files read`
              + ` · ${classification.filled ?? 0} blanks filled · ${classification.unanswered ?? 0} unreadable · ${classification.skipped ?? 0} skipped`}
        </Text>
      )}
    </Box>
  );
};

ImportManifestTools.propTypes = {
  importData: PropTypes.object.isRequired,
  tools: PropTypes.arrayOf(PropTypes.string)
};

export default ImportManifestTools;
